import { Component, DestroyRef, HostBinding, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MdsIconsModule } from '@mds/icons';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ButtonComponent, CheckboxComponent } from '@mds/ui';
import { GlobalDataService } from '@services/global-data.service';
import { MakersiteService } from '@services/makersite.service';
import { HostnameService } from '@services/hostname.service';
import { LocalStorageService } from '@services/local-storage.service';
import { CookiesService } from '@services/cookies.service';
import { SessionTimeoutService } from '@services/session-timeout.service';
import { KnowledgeBaseDialogComponent } from '@dialogs/knowledge-base-dialog/knowledge-base-dialog.component';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from '@services/toaster.service';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { backgroundThemes, EMAIL_PATTERN, Theme } from '@utils/app-auth';
import { MenuService } from '@services/menu.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface SSOLogin {
  name: string;
  api: string;
  logo: string;
}

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    MdsIconsModule,
    FormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    ButtonComponent,
    CheckboxComponent,
    MatButtonModule,
    MatTooltipModule
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  host: { class: 'login' }
})
export class LoginComponent implements OnInit {
  backgroundThemes = backgroundThemes;

  currentTheme!: Theme;
  isLoginForm = true;
  loginForm: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email, Validators.pattern(EMAIL_PATTERN)]),
    password: new FormControl('', [Validators.required]),
    remember: new FormControl(true)
  });
  forgotPasswordForm: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email, Validators.pattern(EMAIL_PATTERN)])
  });
  @HostBinding('style.background') hostBg!: string;
  signIns: SSOLogin[];
  private globalDataService = inject(GlobalDataService);
  private makersiteService = inject(MakersiteService);
  private hostnameService = inject(HostnameService);
  private localStorageService = inject(LocalStorageService);
  private cookiesService = inject(CookiesService);
  private sessionTimeout = inject(SessionTimeoutService);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private menuService = inject(MenuService);
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params: Params) => {
        this.isLoginForm = !params['reset-password'];
      })

    const idx = Math.floor(Math.random() * this.backgroundThemes.length);
    this.currentTheme = this.backgroundThemes[idx];
    this.hostBg = `#003C4F url(${this.currentTheme.image}) no-repeat center/cover`;

    this.globalDataService.setCurrentActiveApp({ name: 'app.login' });
    this.getSingleSingIn();
  }

  onSingleSignIn(signInInfo: any) {
    const baseUrl = this.hostnameService.getOrigin();
    window.location.href = baseUrl + signInInfo.api;
  }

  onSubmit() {
    if (!this.loginForm.invalid) {
      // clear all storage before log in and new token stored
      this.localStorageService.clear();
      sessionStorage.clear();
      this.cookiesService.clearCookies();

      const data = `Basic ${this.hostnameService.base64EncodeUnicode(
        `${this.loginForm.value.email}:${this.loginForm.value.password}`
      )}`;
      const remember = this.loginForm.value.remember ? 1 : 0;
      this.makersiteService.authenticate(data, remember).subscribe(
        (res: any) => {
          try {
            localStorage.setItem('remember', remember.toString());
            localStorage.setItem('auth_token', res.token);
            localStorage.setItem('refresh_token', res.refresh_token);
            localStorage.setItem('auth_rid', res.info.rid);
            this.cookiesService.setCookie('initiateInfoPanel', true, 30);
            this.globalDataService.setTokenString(res.token);
            if (!remember) this.sessionTimeout.initializeSession();
            this.setLocalStorageValue(res);
            this.menuService.initAppsAndImports();
          } catch (error) {
            alert('You are in Privacy Mode\nPlease deactivate Privacy Mode and then reload the page.');
          }
        },
        (err: any) => {
          this.toastr.showMessage(err.error.message, 3000, '', false, 'left', 'bottom', 'light');
        }
      );
    }
  }

  setLocalStorageValue(res: any) {
    const teamListInfo: Record<string, any> = {};
    teamListInfo.teams = res.teams;
    const teamInfo: Record<string, any> = {};
    teamInfo.team_rid = res.team_rid;
    teamInfo.team_name = res.team_name;

    localStorage.removeItem('reset_pass');
    localStorage.setItem('team_info', JSON.stringify(teamInfo));
    localStorage.setItem('teamListInfo', JSON.stringify(teamListInfo));
    localStorage.setItem(
      'user_info',
      JSON.stringify({ ...res.info, opt_out_of_ux_tracking: res.opt_out_of_ux_tracking })
    );
    this.globalDataService.setFeatures();
    this.route.queryParams.subscribe((params: any) => {
      if (params.callback === 'knowledge_base') {
        this.dialog.open(KnowledgeBaseDialogComponent, {
          data: {
            path: '/api/v1/sso-gateway/knowledge-base/token',
            body: { url: params?.kb_fallback_url }
          },
          disableClose: true,
          panelClass: 'unauthorized-error-dialog'
        });
      } else if (params.next) {
        this.router.navigate([params.next]);
      } else {
        const isUserExternalSupplier = (res?.info?.features ?? [])
          .map((feature: { plan: string }) => feature.plan)
          .includes('external_supplier');
        const navigationUrl = isUserExternalSupplier ? '/information-requests/supplier' : '/dashboard';
        this.router.navigate([navigationUrl]);
      }
    });
  }

  onForgotPasswordSubmit() {
    if (!this.forgotPasswordForm.invalid) {
      const email = this.forgotPasswordForm.controls.email.getRawValue();
      this.makersiteService.forgotPassword({ email }).subscribe({
        next: (res: any) => {
          this.toastr.showMessage(
            res?.message ?? 'Success! Password reset link has been sent',
            3000,
            '',
            false,
            'left',
            'bottom',
            'light'
          );
          localStorage.setItem('reset_pass', JSON.stringify(email));
        },
        error: (error) => {
          this.toastr.showMessage(
            error?.error?.message || 'Sorry! Something is wrong.',
            3000,
            '',
            false,
            'left',
            'bottom',
            'light'
          );
        }
      });
    }
  }

  onForgotPassword() {
    this.isLoginForm = false;
    const email = this.loginForm.controls.email.getRawValue() ?? '';
    this.forgotPasswordForm.controls.email.setValue(email);
  }

  private getSingleSingIn() {
    this.makersiteService.singleSingIn().subscribe((res: SSOLogin[]) => {
      this.signIns = res;
    });
  }
}
